import { ReactNode, useMemo, useState } from 'react'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import FFLogo from '@forwardflow/assets/ff-onboarding-logo.svg'
import { Button } from '../ui/primitives'
import { Icon } from '../ui/Icon'
import { Circle, TArt } from '../ui/motion'
import { FooterCtx, IFooter } from './footer-context'

/** The Forward Flow mark, as it is. */
export const Logo = () => (
   <span className="flex items-center min-w-0" aria-label="Forward Flow">
      <img src={FFLogo} alt="Forward Flow" className="w-10 h-10" />
   </span>
)

export const TopBar = ({
   title,
   right,
}: {
   title?: ReactNode
   right?: ReactNode
}) => (
   <header className="h-[72px] shrink-0 border-b border-[var(--ob-border)] flex items-center justify-between px-5 md:px-[28px] gap-4">
      <div className="flex items-center gap-4 min-w-0">
         <Logo />
         <span className="text-[15px] font-semibold text-white truncate">
            {title || 'Set up Forward Flow'}
         </span>
      </div>
      <div className="flex items-center gap-3">{right}</div>
   </header>
)

/**
 * The frame: top bar, a left panel, the content column. The actions sit at
 * the bottom of the content column, Back left, primary right, at the same
 * spot on every screen.
 */
export const SetupFrame = ({
   children,
   title,
   rail,
   right,
}: {
   children: ReactNode
   title?: ReactNode
   rail?: ReactNode
   right?: ReactNode
}) => {
   const [footer, setFooter] = useState<IFooter | null>(null)
   const ctx = useMemo(() => ({ set: setFooter }), [])
   return (
      <FooterCtx.Provider value={ctx}>
         <div
            className="ob fixed inset-0 flex flex-col overflow-hidden"
            data-testid="ob-frame"
         >
            <div
               className="ob-glow fixed inset-0 opacity-[0.05] pointer-events-none"
               aria-hidden
            />
            <TopBar title={title} right={right} />
            <div className="relative flex-1 min-h-0 flex">
               {rail && (
                  <aside className="hidden md:flex w-[280px] lg:w-[300px] shrink-0 border-r border-[var(--ob-border)] flex-col">
                     {rail}
                  </aside>
               )}
               <main className="flex-1 min-w-0 flex flex-col">
                  <div className="flex-1 min-h-0">{children}</div>
                  <div
                     className={cn(
                        'ob-footer h-[88px] md:h-[104px] shrink-0 flex items-center justify-between gap-3 px-4 md:px-[32px] border-t border-[var(--ob-border)] transition-opacity',
                        !footer && 'opacity-0 pointer-events-none'
                     )}
                     data-testid="ob-footer"
                  >
                     <div className="w-12 md:w-[160px] shrink-0">
                        {footer?.back && (
                           <Button
                              variant="secondary"
                              size="lg"
                              className="w-12 md:w-[160px] !px-0 md:!px-6"
                              onClick={footer.back}
                              testId="ob-back"
                              ariaLabel="Back"
                           >
                              <Icon name="arrow-left" size={16} />{' '}
                              <span className="hidden md:inline">Back</span>
                           </Button>
                        )}
                     </div>
                     <div className="hidden md:block min-w-0 text-center px-4">
                        {footer?.hint && (
                           <p
                              className="text-[13px] text-[var(--ob-text-2)] truncate"
                              data-testid="ob-hint"
                           >
                              {footer.hint}
                           </p>
                        )}
                        {footer?.secondary && (
                           <button
                              type="button"
                              onClick={footer.secondary.onClick}
                              className="text-[13px] font-semibold text-[var(--ob-light)] hover:underline mt-0.5"
                              data-testid="ob-secondary"
                           >
                              {footer.secondary.label}
                           </button>
                        )}
                     </div>
                     <div
                        className="flex-1 md:flex-none md:w-[320px] shrink-0 min-w-0"
                        data-testid={footer?.testId}
                     >
                        {footer && (
                           <Button
                              size="lg"
                              full
                              onClick={footer.onAction}
                              disabled={footer.disabled || footer.loading}
                              testId="ob-action"
                           >
                              <span className="truncate">
                                 {footer.loading ? 'One moment' : footer.action}
                              </span>
                              {!footer.loading && (
                                 <Icon
                                    name={footer.icon || 'chevrons'}
                                    size={18}
                                 />
                              )}
                           </Button>
                        )}
                     </div>
                  </div>
               </main>
            </div>
         </div>
      </FooterCtx.Provider>
   )
}

/**
 * One screen: the icon in its disc, the question, one line of context,
 * then the one thing to do, all centered in the content column.
 */
export const Screen = ({
   children,
   kind,
   stepKey,
   title,
   lede,
   width = 'md',
}: {
   children: ReactNode
   kind: TArt
   stepKey: string
   title: ReactNode
   lede?: ReactNode
   width?: 'sm' | 'md' | 'lg'
}) => (
   <div className="h-full overflow-y-auto overflow-x-hidden ob-scroll">
      <AnimatePresence mode="wait" initial={false}>
         <motion.section
            key={stepKey}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="ob-screen min-h-full flex flex-col items-center px-5 md:px-8 pt-[28px] pb-6"
            data-testid={'ob-screen-' + stepKey}
         >
            <Circle kind={kind} />
            <h1 className="ob-title mt-4 text-[28px] md:text-[34px] leading-[1.12] font-semibold tracking-[-0.02em] text-center [text-wrap:balance]">
               {title}
            </h1>
            {lede && (
               <p className="mt-3 text-[15px] md:text-[16px] leading-6 text-[var(--ob-text-2)] text-center max-w-[62ch] [text-wrap:pretty]">
                  {lede}
               </p>
            )}
            <div
               className={cn(
                  'ob-body mt-7 w-full',
                  width === 'sm' && 'max-w-[460px]',
                  width === 'md' && 'max-w-[580px]',
                  width === 'lg' && 'max-w-[780px]'
               )}
            >
               {children}
            </div>
         </motion.section>
      </AnimatePresence>
   </div>
)
